/**
 * usePermissions — centralised, memoised permission hook.
 *
 * Returns three checkers:
 *   can(perm)         → true if user holds this single permission
 *   canAny(perms[])   → true if user holds at least one (OR)
 *   canAll(perms[])   → true if user holds every one (AND)
 *
 * Super-privileged roles (SUPER_ADMIN_BYPASS_ROLES) bypass all checks.
 *
 * Usage:
 *   const { can, canAny, canAll } = usePermissions();
 *   can(PERMISSIONS.FEE_COLLECT)
 *   canAny([PERMISSIONS.FEE_VIEW_ALL, PERMISSIONS.FEE_COLLECT])
 *   canAll([PERMISSIONS.FEE_STRUCTURE_CREATE, PERMISSIONS.FEE_STRUCTURE_ASSIGN])
 */

import { useCallback, useMemo } from "react";
import { useUser } from "../../app/providers/userContext";
import { SUPER_ADMIN_BYPASS_ROLES } from "../constants/permissions";

export const usePermissions = () => {
  const { user } = useUser();

  // Stable reference to the permission array — avoids re-creating checkers
  // when unrelated user fields change.
  const permissionSet = useMemo(
    () => new Set(user?.permission ?? []),
    [user?.permission],
  );

  /**
   * True when the user holds a bypass role (principal, admin, school_admin).
   * Computed once per permission-set change.
   */
  const isPrivileged = useMemo(
    () => SUPER_ADMIN_BYPASS_ROLES.some((r) => permissionSet.has(r)),
    [permissionSet],
  );

  /** Check a single permission code. */
  const can = useCallback(
    (permission) => {
      if (!user?.permission) return false;
      if (isPrivileged) return true;
      return permissionSet.has(permission);
    },
    [user?.permission, isPrivileged, permissionSet],
  );

  /** True if the user holds ANY of the listed permissions (OR logic). */
  const canAny = useCallback(
    (permissions) => {
      if (!user?.permission) return false;
      if (isPrivileged) return true;
      return permissions.some((p) => permissionSet.has(p));
    },
    [user?.permission, isPrivileged, permissionSet],
  );

  /** True if the user holds ALL of the listed permissions (AND logic). */
  const canAll = useCallback(
    (permissions) => {
      if (!user?.permission) return false;
      if (isPrivileged) return true;
      return permissions.every((p) => permissionSet.has(p));
    },
    [user?.permission, isPrivileged, permissionSet],
  );

    const hasPermission = (permission) => {
      if (Array.isArray(permission)) return canAny(permission);
      return can(permission);
    };
  return { can, canAny, canAll, hasPermission };
};